import type { FeatureFlag } from "@/types/feature-flags"
import { mockFeatureFlags } from "@/lib/mock-feature-flags"
import { env } from "@/lib/env"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"

// Maximum number of retry attempts
const MAX_RETRIES = 2

// Feature flag service that handles fetching feature flags
export const featureFlagService = {
  // Get feature flags from the backend or mock data
  getFeatureFlags: async (token: string | null): Promise<FeatureFlag[]> => {
    // If we're in development mode and USE_MOCK_DATA is true, return mock data
    if (process.env.NODE_ENV === "development" && env.useMockData === "true") {
      console.log("Using mock feature flags data")
      return mockFeatureFlags
    }

    // If no token is provided, throw an error
    if (!token) {
      throw new Error("Authentication token is required to fetch feature flags")
    }

    // Try to fetch feature flags from the backend with retries
    return fetchWithRetry(
      `${env.adminServiceApiEndpoint}/globalConfiguration/FeatureFlag/FeatureConfiguration`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
      MAX_RETRIES,
    )
  },
}

// Helper function to fetch with retry logic and token management
async function fetchWithRetry<T>(url: string, options: RequestInit, maxRetries: number): Promise<T> {
  let lastError: Error | null = null

  // Try the request up to maxRetries times
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, options)

      if (!response.ok) {
        // Handle 401 Unauthorized errors by requesting a new token and queuing for retry
        if (response.status === 401) {
          console.warn("Received 401 Unauthorized in feature flag service from", url)

          // Only request a new token if we haven't already requested one
          if (tokenManager.shouldRequestToken()) {
            console.log("Requesting new token from parent")
            requestTokenDueToAuthFailure(`401 Unauthorized from ${url}`)
          }

          // Queue this request to be retried when we get a new token
          console.log("Queueing feature flag request for retry when new token is received")
          const retriedResponse = await tokenManager.queueRequest(url, options)

          if (!retriedResponse.ok) {
            throw new Error(`HTTP error ${retriedResponse.status}: ${retriedResponse.statusText}`)
          }

          return await retriedResponse.json()
        }

        throw new Error(`HTTP error ${response.status}: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error(`Attempt ${attempt + 1}/${maxRetries} failed:`, error)
      lastError = error instanceof Error ? error : new Error(String(error))

      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries - 1) {
        // Exponential backoff: wait longer between each retry
        const delay = Math.pow(2, attempt) * 500 // 500ms, 1000ms, 2000ms, etc.
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  // If we've exhausted all retries, throw the last error
  throw lastError || new Error("Failed to fetch after multiple attempts")
}
